import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { LoggerService } from '../../../../services/logger.service';

interface SettingOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-calendar-display-settings',
  templateUrl: './calendar-display-settings.component.html',
  styleUrls: ['./calendar-display-settings.component.css']
})
export class CalendarDisplaySettingsComponent implements OnInit {
  configFieldName = 'field_day_view_reference';

  settingsForm: FormGroup;
  showProgressSpinner = false;
  successMessage = '';
  errorMessage = '';

  // Option lists mirror the backend list_string allowed_values on
  // configuration/day_view_settings. Keep these in sync with the field config.
  timeFormatOptions: SettingOption[] = [
    { value: '12h', label: '12-hour' },
    { value: '24h', label: '24-hour' },
  ];

  defaultViewOptions: SettingOption[] = [
    { value: 'month', label: 'Month' },
    { value: 'agendaWeek', label: 'Week' },
    { value: 'agendaDay', label: 'Day' },
    { value: 'listWeek', label: 'List' },
  ];

  timeIncrementOptions: SettingOption[] = [
    { value: '00:15:00', label: '15 minutes' },
    { value: '00:30:00', label: '30 minutes' },
    { value: '01:00:00', label: '60 minutes' },
  ];

  weekStartOptions: SettingOption[] = [
    { value: '0', label: 'Sunday' },
    { value: '1', label: 'Monday' },
  ];

  eventEditingOptions: SettingOption[] = [
    { value: 'view', label: 'View only' },
    { value: 'drag', label: 'Drag events' },
    { value: 'drag_resize', label: 'Drag and resize' },
  ];

  /**
   * Default values, matching the `default_value` set on each field in
   * configuration/day_view_settings. Single source of truth for the form's
   * initial state, load-time fallbacks, and the "Reset to defaults" action.
   */
  private readonly defaults = {
    field_time_format: '12h',
    field_default_view: 'agendaDay',
    field_duration_time_slot: '00:30:00',
    field_week_start: '0',
    field_show_weekends: true,
    field_event_editing: 'drag_resize',
    field_allow_overlap: true,
    field_all_day_slot: true,
  };

  constructor(
    private fb: FormBuilder,
    private drupalRESTService: DrupalRESTService
  ) {
    this.settingsForm = this.fb.group({ ...this.defaults });
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  /** Pull the first scalar value out of a Drupal field array, e.g. [{value: 'x'}] => 'x'. */
  private fieldValue(field: any, fallback: any): any {
    if (Array.isArray(field) && field.length && field[0]?.value !== undefined) {
      return field[0].value;
    }
    return fallback;
  }

  /** Booleans come back as '0'/'1' strings from Drupal. Normalize to real booleans. */
  private fieldBool(field: any, fallback: boolean): boolean {
    if (Array.isArray(field) && field.length && field[0]?.value !== undefined) {
      return field[0].value === '1' || field[0].value === 1 || field[0].value === true;
    }
    return fallback;
  }

  loadSettings(): void {
    this.showProgressSpinner = true;
    this.errorMessage = '';

    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${this.configFieldName}`)
      .subscribe(
        (response: any) => {
          if (response && !response.error) {
            this.settingsForm.patchValue({
              field_time_format: this.fieldValue(response.field_time_format, this.defaults.field_time_format),
              field_default_view: this.fieldValue(response.field_default_view, this.defaults.field_default_view),
              field_duration_time_slot: this.fieldValue(response.field_duration_time_slot, this.defaults.field_duration_time_slot),
              field_week_start: this.fieldValue(response.field_week_start, this.defaults.field_week_start),
              field_show_weekends: this.fieldBool(response.field_show_weekends, this.defaults.field_show_weekends),
              field_event_editing: this.fieldValue(response.field_event_editing, this.defaults.field_event_editing),
              field_allow_overlap: this.fieldBool(response.field_allow_overlap, this.defaults.field_allow_overlap),
              field_all_day_slot: this.fieldBool(response.field_all_day_slot, this.defaults.field_all_day_slot),
            });
            this.settingsForm.markAsPristine();
          }
          this.showProgressSpinner = false;
        },
        (error) => {
          LoggerService.error('Error loading calendar display settings:', error);
          this.errorMessage = 'Failed to load calendar display settings.';
          this.showProgressSpinner = false;
        }
      );
  }

  /**
   * Restore every setting to its field default. Client-side only. The user
   * must Save to persist. Marks the form dirty so the change is reviewable
   * and the Save button enables.
   */
  resetToDefaults(): void {
    this.settingsForm.reset({ ...this.defaults });
    this.settingsForm.markAsDirty();
    this.successMessage = '';
    this.errorMessage = '';
  }

  onSubmit(): void {
    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    // Booleans are stored as '0'/'1' on the boolean fields.
    const value = this.settingsForm.value;
    const config_data = {
      field_time_format: value.field_time_format,
      field_default_view: value.field_default_view,
      field_duration_time_slot: value.field_duration_time_slot,
      field_week_start: value.field_week_start,
      field_show_weekends: value.field_show_weekends ? '1' : '0',
      field_event_editing: value.field_event_editing,
      field_allow_overlap: value.field_allow_overlap ? '1' : '0',
      field_all_day_slot: value.field_all_day_slot ? '1' : '0',
    };

    this.drupalRESTService
      .httpPOST('/api_rest/v1/saveStudioConfig', {
        config_field_name: this.configFieldName,
        config_data,
      })
      .subscribe(
        (response: any) => {
          if (response && response.error) {
            this.errorMessage = 'Failed to save: ' + response.error;
          } else {
            this.successMessage = 'Calendar display settings saved.';
            this.settingsForm.markAsPristine();
          }
          this.showProgressSpinner = false;
        },
        (error) => {
          LoggerService.error('Error saving calendar display settings:', error);
          this.errorMessage = 'Failed to save calendar display settings.';
          this.showProgressSpinner = false;
        }
      );
  }
}
